import React, { useState } from 'react';
import {
    User as PersonOutlineOutlinedIcon,
    Check as DoneIcon,
    CheckCircle as CheckCircleIcon,
    ChevronDown as ExpandMoreIcon,
    ChevronUp as ExpandLessIcon,
} from 'lucide-react';

import moment from 'moment';

import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import { useAppDispatch, useAppSelector } from 'src/store';
import { reset } from 'src/app/projects/views/new-project-definition/state/newProjectDefinitionSlice';
import { getCurrentCustomerDetails } from 'src/app/customers/state/customersSelector';
import { availableAreaColors } from 'src/shared/constants/colors';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import useProjectDefinition from 'src/app/projects/views/new-project-definition/hooks/useProjectDefinition';
import Modal from 'src/shared/components/modal/Modal';
import { STANDARD_DATE_FORMAT } from 'src/shared/constants/dateConstants';
import { useLazyProjectsQuery } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import { getFeaturesDisplayText, hasInsightsFeature, hasHabitatInsightsFeature, isBasicProject } from 'src/shared/utils/featureHelpers';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { GNPViews } from 'src/routes/routesConfig';
import ErrorMessage from 'src/shared/components/error-msg/ErrorMessage';
import { formatError } from 'src/shared/utils/errorHelpers';
import TestTypeChip from 'src/shared/components/test-type-chip/TestTypeChip';
import styles from './ReviewAndSubmit.module.scss';
import { validateSamplingEvent } from '../sampling-cadence/samplingCadence.utils';

const ReviewAndSubmit = () => {
    const { formState, isEditMode, submitProjectDefinitionForm, isFormValid } = useProjectDefinition();

    const navigate = useAppNavigation();
    const currentCustomerDetails = useAppSelector(getCurrentCustomerDetails) || { customerName: '', customerId: '' };
    const [refetchProjects] = useLazyProjectsQuery();

    const [shouldShowModal, showModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const dispatch = useAppDispatch();

    const onSubmit = async () => {
        setErrorMessage(null);
        setIsSubmitting(true);
        try {
            await submitProjectDefinitionForm();
            showModal(true);
        } catch (error: any) {
            console.error('Error submitting project definition:', error);
            const errorMsg = formatError(error);
            setErrorMessage(errorMsg || 'An error occurred while submitting the project definition. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const backToProjectAdmin = () => {
        dispatch(reset());
        refetchProjects({
            customerId: currentCustomerDetails.customerId,
        });

        navigate.to(
            navigate.getPath(GNPViews.ORG_MANAGEMENT, {
                customerId: currentCustomerDetails.customerId,
            }),
            { state: { sourcePage: 'new-project-definition' } }
        );
    };

    const {
        projectName,
        projectCode,
        features,
        country,
        projectLeadRole,
        samplingEvents,
        sampleGroups,
        habitatAssayTypes,
        projectLeadEmail,
        projectLeadName,
        habitatInsightsUploadedFileContent,
        habitatInsightsSites,
    } = formState;

    const successMessageText = isEditMode
        ? 'Project definition successfully edited'
        : 'You have successfully saved a new project definition';

    const [isValidationExpanded, setIsValidationExpanded] = useState(false);

    const getValidationErrors = (): string[] => {
        const errors: string[] = [];

        // Project details validation
        if (!projectName) errors.push('Project name is required');
        if (!country) errors.push('Sampling country is required');
        if (!projectLeadRole) errors.push('Customer contact job title is required');

        // Project aims validation - only if EDNA feature is selected
        // Note: Sample groups and test types are no longer required
        if (hasInsightsFeature(features || [])) {
            // No validation required - sample groups and test types are optional
        }

        // Sampling cadence validation - only if EDNA feature is selected
        if (hasInsightsFeature(features || [])) {
            // Validate existing sampling events if any
            if (samplingEvents.length > 0) {
                const invalidEvents = samplingEvents
                    .map((event, index) => ({ event, index, validation: validateSamplingEvent(formState, index) }))
                    .filter(({ validation }) => validation.hasError);
                if (invalidEvents.length > 0) {
                    invalidEvents.forEach(({ index }) => {
                        const eventYear = samplingEvents[index].year || moment(samplingEvents[index].fromDate || new Date()).year();
                        errors.push(`Sampling event in year ${eventYear} has invalid or missing information`);
                    });
                }
            }
        }

        return errors;
    };

    const validationErrors = getValidationErrors();
    const isValid = validationErrors.length === 0;
    const shouldShowSamplingAndTests = hasInsightsFeature(features || []) || isBasicProject(features || []);

    return (
        <>
            <div className={styles.reviewAndSubmitWrapper}>
                <div className={styles.reviewSection}>
                    <div className={styles.reviewTitle}>Review</div>

                    <div className={styles.reviewContent}>
                        <div className={styles.reviewGroup}>
                            <div className={styles.reviewRow}>
                                <span className={styles.reviewLabel}>Customer name</span>
                                <span className={styles.reviewValue}>
                                    {currentCustomerDetails.customerName || <span className={styles.emptyValue}>Not set</span>}
                                </span>
                            </div>
                            <div className={styles.reviewRow}>
                                <span className={styles.reviewLabel}>Project name</span>
                                <span className={styles.reviewValue}>
                                    {projectName || <span className={styles.emptyValue}>Not set</span>}
                                </span>
                            </div>
                            <div className={styles.reviewRow}>
                                <span className={styles.reviewLabel}>Project code</span>
                                <span className={styles.reviewValue}>
                                    {projectCode || <span className={styles.emptyValue}>Not set</span>}
                                </span>
                            </div>
                            <div className={styles.reviewRow}>
                                <span className={styles.reviewLabel}>Features</span>
                                <span className={styles.reviewValue}>
                                    {features && features.length > 0 ? (
                                        getFeaturesDisplayText(features)
                                    ) : (
                                        <span className={styles.emptyValue}>Not set</span>
                                    )}
                                </span>
                            </div>
                            <div className={styles.reviewRow}>
                                <span className={styles.reviewLabel}>Sampling country</span>
                                <span className={styles.reviewValue}>{country || <span className={styles.emptyValue}>Not set</span>}</span>
                            </div>
                            <div className={styles.reviewRow}>
                                <span className={styles.reviewLabel}>Customer contact</span>
                                <div className={styles.contactInfo}>
                                    <PersonOutlineOutlinedIcon className={styles.contactIcon} />
                                    <div>
                                        <div className={styles.contactName}>
                                            {projectLeadName || <span className={styles.emptyValue}>Not set</span>}
                                        </div>
                                        <div className={styles.contactEmail}>
                                            {projectLeadEmail || <span className={styles.emptyValue}>Not set</span>}
                                        </div>
                                        {projectLeadRole ? (
                                            <div className={styles.contactRole}>{projectLeadRole}</div>
                                        ) : (
                                            <div className={styles.emptyValue}>Not set</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {shouldShowSamplingAndTests && (
                            <div className={styles.reviewGroup}>
                                <div className={styles.reviewRow}>
                                    <span className={styles.reviewLabel}>
                                        Sample groups <QuestionButtonHelp type='api' slug='project-definition-sample-groups' />
                                    </span>
                                    <div className={styles.sampleGroups}>
                                        {sampleGroups && sampleGroups.length > 0 ? (
                                            sampleGroups.map((entry, index) => (
                                                <span key={index} className={styles.sampleGroupTag}>
                                                    <span
                                                        className={styles.sampleGroupColor}
                                                        style={{ backgroundColor: availableAreaColors[index] }}
                                                    ></span>
                                                    {entry}
                                                </span>
                                            ))
                                        ) : (
                                            <span className={styles.emptyValue}>Not set</span>
                                        )}
                                    </div>
                                </div>
                                <div className={styles.reviewRow}>
                                    <span className={styles.reviewLabel}>
                                        Test <QuestionButtonHelp type='api' slug='project-definition-habitat-test-type' />
                                    </span>
                                    <div className={styles.habitatAssayTypes}>
                                        {habitatAssayTypes && habitatAssayTypes.length > 0 ? (
                                            habitatAssayTypes.map((entry, index) => (
                                                <TestTypeChip key={index} testTypeKey={entry.habitatAssayKey} />
                                            ))
                                        ) : (
                                            <span className={styles.emptyValue}>Not set</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {shouldShowSamplingAndTests && (
                            <div className={styles.reviewGroup}>
                                {samplingEvents && samplingEvents.length > 0 ? (
                                    (() => {
                                        // Group events by year
                                        const eventsByYear: Record<number, typeof samplingEvents> = {};
                                        samplingEvents.forEach(event => {
                                            const year = event.year || moment(event.fromDate || new Date()).year();
                                            if (!eventsByYear[year]) {
                                                eventsByYear[year] = [];
                                            }
                                            eventsByYear[year].push(event);
                                        });
                                        const years = Object.keys(eventsByYear)
                                            .map(Number)
                                            .sort((a, b) => b - a);

                                        return years.map(year => (
                                            <div key={year} className={styles.reviewRow}>
                                                <span className={styles.reviewLabel}>Year {year} events</span>
                                                <div className={styles.samplingEventsList}>
                                                    {eventsByYear[year].map((entry, index) => (
                                                        <div key={index} className={styles.samplingEventItem}>
                                                            <span className={styles.eventName}>{entry.name}</span>
                                                            <span className={styles.eventDates}>
                                                                {moment(entry.fromDate).format(STANDARD_DATE_FORMAT)} -{' '}
                                                                {moment(entry.toDate).format(STANDARD_DATE_FORMAT)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ));
                                    })()
                                ) : (
                                    <div className={styles.reviewRow}>
                                        <span className={styles.reviewLabel}>Sampling events</span>
                                        <span className={styles.emptyValue}>Not set</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {hasHabitatInsightsFeature(features || []) && (
                            <div className={styles.reviewGroup}>
                                <div className={styles.reviewRow}>
                                    <span className={styles.reviewLabel}>Habitat Insights</span>
                                    <div className={styles.habitatInsightsReview}>
                                        {habitatInsightsUploadedFileContent || (habitatInsightsSites && habitatInsightsSites.length > 0) ? (
                                            <div>
                                                {habitatInsightsUploadedFileContent && (
                                                    <div className={styles.reviewRow}>
                                                        <span className={styles.reviewLabel}>File uploaded</span>
                                                        <span className={styles.reviewValue}>Yes</span>
                                                    </div>
                                                )}
                                                {habitatInsightsSites && habitatInsightsSites.length > 0 && (
                                                    <div className={styles.reviewRow}>
                                                        <span className={styles.reviewLabel}>Areas of interest</span>
                                                        <span className={styles.reviewValue}>{habitatInsightsSites.length} site(s)</span>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className={styles.emptyValue}>Not set</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.validationStatus}>
                    <div className={styles.validationHeader} onClick={() => setIsValidationExpanded(!isValidationExpanded)}>
                        <div className={styles.validationHeaderContent}>
                            {isValid ? (
                                <>
                                    <CheckCircleIcon className={styles.validationIconSuccess} />
                                    <span className={styles.validationTitle}>All required fields are completed</span>
                                </>
                            ) : (
                                <>
                                    <span className={styles.validationIconError}>!</span>
                                    <span className={styles.validationTitle}>
                                        {validationErrors.length} {validationErrors.length === 1 ? 'issue' : 'issues'} need to be resolved
                                    </span>
                                </>
                            )}
                        </div>
                        {isValidationExpanded ? (
                            <ExpandLessIcon className={styles.expandIcon} />
                        ) : (
                            <ExpandMoreIcon className={styles.expandIcon} />
                        )}
                    </div>
                    {isValidationExpanded && (
                        <div className={styles.validationContent}>
                            {isValid ? (
                                <div className={styles.validationSuccess}>
                                    <p>All required information has been provided. You can submit the project definition.</p>
                                </div>
                            ) : (
                                <ul className={styles.validationErrors}>
                                    {validationErrors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>

                {errorMessage && (
                    <div className={styles.errorSection}>
                        <ErrorMessage message={<div style={{ whiteSpace: 'pre-line' }}>{errorMessage}</div>} />
                    </div>
                )}

                <div className={styles.bottomNavigation}>
                    <SecondaryButton onClick={backToProjectAdmin} disabled={isSubmitting}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton onClick={onSubmit} disabled={!isFormValid() || isSubmitting}>
                        {isSubmitting ? 'Submitting...' : isEditMode ? 'Save changes' : 'Submit'}
                    </PrimaryButton>
                </div>
            </div>
            {shouldShowModal && (
                <Modal>
                    <div className={styles.successModal}>
                        <div className={styles.successTitle}>Success!</div>
                        <div className={styles.successContent}>
                            <span className={styles.successTickIcon}>
                                <DoneIcon />
                            </span>
                            <span className={styles.successMessage}>{successMessageText}</span>
                        </div>
                        <div>
                            <PrimaryButton onClick={backToProjectAdmin} className={styles.backButton}>
                                Back to project admin
                            </PrimaryButton>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default ReviewAndSubmit;
